import { useCallback, useEffect, useState } from 'react';
import { httpClient } from '@/infrastructure/http';

export type PanelNovedadTipo = 'CONSULTORIO_EXTRA' | 'PROFESIONAL_EXTRA';

export type PanelNovedad = {
  id: number;
  tenantId: string;
  nombreClinica: string;
  tipo: PanelNovedadTipo;
  precioUnitario: number;
  cantidadNueva?: number;
  fechaCreacion: string;
};

export function novedadCopyKey(tipo: PanelNovedadTipo): string {
  return tipo === 'PROFESIONAL_EXTRA'
    ? 'pages.dashboard.novedades.profesional'
    : 'pages.dashboard.novedades.consultorio';
}

export function formatNovedadPrice(precio: number): string {
  return `$${Number(precio || 0).toLocaleString()}`;
}

const POLL_MS = 60_000;

export function usePanelNovedades(limit = 20, options?: { poll?: boolean }) {
  const poll = options?.poll !== false;
  const [novedades, setNovedades] = useState<PanelNovedad[]>([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await httpClient.get<PanelNovedad[]>(`/novedades?limit=${limit}`);
      setNovedades(res.data ?? []);
      setError(false);
    } catch {
      setNovedades([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    void load();
    if (!poll) return undefined;
    const timer = window.setInterval(() => {
      void load();
    }, POLL_MS);
    const onFocus = () => {
      void load();
    };
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [load, poll]);

  return { novedades, error, loading, refetch: load };
}
